import { List, Minus, Plus, RefreshCw } from "lucide-react-native";
import { FC } from "react";

import { HStack, Pressable, Text, VStack } from "@/components/ui";
import { useLocalization } from "@/hooks";

export type RotationField = "cells_per_respondent" | "items_per_cell";

interface RotationConfig {
  cells_per_respondent?: number | null;
  items_per_cell?: number | null;
}

interface RotationControlsProps {
  config: RotationConfig | null;
  /** Which fields are tunable, in display order. */
  fields: RotationField[];
  /** Inclusive bounds (already clamped to the current cell count if dynamic). */
  getBounds: (field: RotationField) => { max: number; min: number };
  labels: {
    cellsPerRespondent: string;
    cellsPerRespondentHint: (count: number) => string;
    itemsPerCell: string;
    itemsPerCellHint: (count: number) => string;
  };
  onChange: (config: RotationConfig) => void;
}

/**
 * Stepper row for the rotation knobs — `cells_per_respondent` (how many cells
 * each persona is shown) and `items_per_cell` (how many items share a screen,
 * for MaxDiff-style tasks). Shared by the section and question editors.
 */
const RotationControls: FC<RotationControlsProps> = ({
  config,
  fields,
  getBounds,
  labels,
  onChange,
}) => {
  const { i18n } = useLocalization();
  if (fields.length === 0) return null;

  const step = (field: RotationField, delta: number) => {
    const bounds = getBounds(field);
    const current = config?.[field] ?? bounds.min;
    const next = Math.max(bounds.min, Math.min(bounds.max, current + delta));
    onChange({ ...config, [field]: next });
  };

  return (
    <VStack className="gap-3">
      <Text className="text-sm font-semibold text-typography-800">
        {i18n.t("studies.sectionEditor.settings")}
      </Text>
      <VStack className="gap-3 pl-4">
        {fields.map((field) => {
          const bounds = getBounds(field);
          const rawValue = config?.[field] ?? bounds.min;
          const value = Math.max(bounds.min, Math.min(bounds.max, rawValue));
          const label =
            field === "cells_per_respondent"
              ? labels.cellsPerRespondent
              : labels.itemsPerCell;
          const hint =
            field === "cells_per_respondent"
              ? labels.cellsPerRespondentHint(value)
              : labels.itemsPerCellHint(value);
          const HintIcon = field === "cells_per_respondent" ? RefreshCw : List;

          return (
            <HStack key={field} className="items-center gap-3">
              <VStack className="flex-1 gap-0.5">
                <Text className="text-sm font-medium text-typography-800">
                  {label}
                </Text>
                <HStack className="items-center gap-1">
                  <HintIcon
                    color="rgb(var(--color-typography-400))"
                    size={11}
                  />
                  <Text className="text-xs text-typography-400">{hint}</Text>
                </HStack>
              </VStack>
              <HStack className="items-center gap-1 border border-outline-200 rounded-lg bg-background-50 p-1">
                <Pressable
                  className="w-7 h-7 items-center justify-center rounded-md hover:bg-outline-100 disabled:opacity-30"
                  disabled={value <= bounds.min}
                  onPress={() => step(field, -1)}
                >
                  <Minus color="rgb(var(--color-typography-700))" size={14} />
                </Pressable>
                <Text className="text-sm font-semibold text-typography-900 w-7 text-center tabular-nums">
                  {value}
                </Text>
                <Pressable
                  className="w-7 h-7 items-center justify-center rounded-md hover:bg-outline-100 disabled:opacity-30"
                  disabled={value >= bounds.max}
                  onPress={() => step(field, 1)}
                >
                  <Plus color="rgb(var(--color-typography-700))" size={14} />
                </Pressable>
              </HStack>
            </HStack>
          );
        })}
      </VStack>
    </VStack>
  );
};

export default RotationControls;
